import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faTimes } from "@fortawesome/free-solid-svg-icons";
import { formatOrgTypesLabel, orgMatchesOrgTypeFilterFlexible } from "../../utils/orgTypeHelpers";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import autoTable from "jspdf-autotable";
import "../css/AdvancedSearch.css";
import "../css/OrganisationList.css";
import "../css/EmailCopyControl.css";
import { resolvePersonImageUrl } from "../../utils/resolvePersonImageUrl";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const NONE_KEY = "__none__";

/** Table look: gold header, beige rows, stable layout (no hover shift) */
const advancedSearchTableStyles = {
  tableWrapper: {
    style: {
      backgroundColor: "#fffbf4",
      borderRadius: "0",
    },
  },
  table: {
    style: {
      backgroundColor: "#fffbf4",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#d4af37",
      minHeight: "48px",
      borderBottom: "1px solid #b8962e",
    },
  },
  headCells: {
    style: {
      color: "#fff",
      fontWeight: "600",
      fontSize: "13px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      textAlign: "left",
      backgroundColor: "#d4af37",
      padding: "10px",
    },
  },
  rows: {
    style: {
      borderBottom: "1px solid #efe6d6",
      minHeight: "52px",
      fontSize: "13px",
    },
  },
  cells: {
    style: {
      backgroundColor: "inherit",
      color: "#333",
      padding: "10px",
      display: "block",
      textAlign: "left",
    },
  },
  pagination: {
    style: {
      backgroundColor: "#fffbf4",
      borderTop: "1px solid #efe6d6",
      minHeight: "52px",
      fontSize: "13px",
    },
  },
};

function countryIdKey(entity) {
  const c = entity?.country_id;
  if (c == null) return "";
  if (typeof c === "object" && c._id != null) return String(c._id);
  return String(c);
}

function continentKey(entity) {
  const c = entity?.country_id;
  if (c == null || typeof c !== "object") return "";
  const cont = c.continent;
  if (cont == null || cont === "") return "";
  return String(cont);
}

/** Resolve continent from populated doc or from countries collection (id → continent). */
function resolvedContinentKey(entity, continentByCountryId) {
  const direct = continentKey(entity);
  if (direct) return direct;
  const cid = countryIdKey(entity);
  if (!cid || !continentByCountryId || typeof continentByCountryId !== "object") return "";
  const cont = continentByCountryId[cid];
  if (cont == null || cont === "") return "";
  return String(cont);
}

/** Build sidebar buckets: { key, label, count }[] */
function buildOrgTypeBuckets(orgs) {
  const counts = new Map();
  const labels = new Map();
  for (const org of orgs) {
    const types = Array.isArray(org.org_type_id) ? org.org_type_id : org.org_type_id ? [org.org_type_id] : [];
    if (!types.length) {
      counts.set(NONE_KEY, (counts.get(NONE_KEY) || 0) + 1);
      labels.set(NONE_KEY, "No data");
    } else {
      for (const t of types) {
        const id =
          t?._id != null ? String(t._id) : `type:${t?.org_type || "Unknown"}`;
        const label = t?.org_type || "Unknown";
        labels.set(id, label);
        counts.set(id, (counts.get(id) || 0) + 1);
      }
    }
  }
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: labels.get(key) || key, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Count rows per continent; list every unique continent from catalog (0 if none in data). */
function buildContinentBuckets(rows, continentByCountryId, catalogContinents) {
  const counts = new Map();
  for (const row of rows) {
    const raw = resolvedContinentKey(row, continentByCountryId);
    const key = raw || NONE_KEY;
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  const items = [];
  const seen = new Set();
  for (const cont of catalogContinents) {
    if (cont == null || cont === "") continue;
    const c = String(cont);
    seen.add(c);
    items.push({ key: c, label: c, count: counts.get(c) || 0 });
  }
  for (const [k, n] of counts.entries()) {
    if (k === NONE_KEY || seen.has(k)) continue;
    seen.add(k);
    items.push({ key: k, label: k, count: n });
  }
  const noneCount = counts.get(NONE_KEY) || 0;
  if (noneCount > 0) {
    items.push({ key: NONE_KEY, label: "No data", count: noneCount });
  }
  return items.sort((a, b) => a.label.localeCompare(b.label));
}

function buildCountryBuckets(orgs) {
  const counts = new Map();
  const labels = new Map();
  for (const org of orgs) {
    const id = countryIdKey(org);
    if (!id) {
      counts.set(NONE_KEY, (counts.get(NONE_KEY) || 0) + 1);
      labels.set(NONE_KEY, "No data");
    } else {
      const c = org.country_id;
      const name = typeof c === "object" && c?.name ? `${c.name}${c.code ? ` - ${c.code}` : ""}` : id;
      labels.set(id, name);
      counts.set(id, (counts.get(id) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: labels.get(key) || key, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** LA 2028 / generic status: "1" = Yes, "0" = No, __none__ = unknown */
function entityStatusKey(row) {
  if (row?.status === 1 || row?.status === "1") return "1";
  if (row?.status === 0 || row?.status === "0") return "0";
  return NONE_KEY;
}

/** Lowercase haystack: resolved continent + populated country.continent (deduped). */
function continentFilterHaystack(entity, continentByCountryId) {
  const parts = [];
  const resolved = resolvedContinentKey(entity, continentByCountryId);
  if (resolved) parts.push(String(resolved));
  const c = entity?.country_id;
  if (typeof c === "object" && c != null && c.continent != null && c.continent !== "") {
    const d = String(c.continent);
    if (!parts.length || parts[0].toLowerCase() !== d.toLowerCase()) parts.push(d);
  }
  return parts.join(" ").toLowerCase();
}

/**
 * Partial continent match: row continent text includes selected needle (case-insensitive).
 * Matches `filters.continent.some((cont) => rowContinentLower.includes(cont.toLowerCase()))`.
 */
function continentFilterMatches(entity, continentByCountryId, selectedContinents) {
  if (!selectedContinents.length) return true;
  const raw = resolvedContinentKey(entity, continentByCountryId);
  const haystack = continentFilterHaystack(entity, continentByCountryId);
  return selectedContinents.some((sel) => {
    if (sel === NONE_KEY) return raw === "";
    const needle = String(sel).toLowerCase();
    if (!needle) return false;
    return haystack.includes(needle);
  });
}

/** Name, code, and id for substring / partial country filter. */
function countryFilterHaystack(entity) {
  const parts = [];
  const c = entity?.country_id;
  if (typeof c === "object" && c != null) {
    if (c.name) parts.push(String(c.name));
    if (c.code) parts.push(String(c.code));
    if (c._id != null) parts.push(String(c._id));
  } else if (c != null && c !== "") {
    parts.push(String(c));
  }
  return parts.join(" ").toLowerCase();
}

/**
 * Partial country match: row country haystack includes filter text, or exact id when filter is 24-hex.
 * Mirrors `filters.country.some((country) => rowCountryLower.includes(country.toLowerCase()))`.
 */
function countryFilterMatches(entity, selectedCountries) {
  if (!selectedCountries.length) return true;
  const haystack = countryFilterHaystack(entity);
  const cid = countryIdKey(entity);
  return selectedCountries.some((sel) => {
    if (sel === NONE_KEY) return !cid;
    const needle = String(sel).toLowerCase();
    if (!needle) return false;
    if (/^[a-f0-9]{24}$/i.test(needle)) return cid === sel;
    return haystack.includes(needle);
  });
}

/**
 * Combined AND filtering — same idea as:
 *
 *   data.filter((item) =>
 *     (filters.orgType.length === 0 || …) &&
 *     (filters.continent.length === 0 || …) &&
 *     (filters.country.length === 0 || …)
 *   );
 *
 * Rows from the API are not flat: org type lives on `org_type_id`, country on `country_id` (id keys match
 * sidebar buckets), continent is resolved from populated country or the countries catalog.
 *
 * Within orgType, multiple selections match if any pass (OR), using id / type: keys and
 * case-insensitive substring match on the joined org type labels (see orgMatchesOrgTypeFilterFlexible).
 */
function organisationMatchesFilters(org, selections, continentByCountryId) {
  const orgKeys = Array.isArray(selections.selectedOrgTypes) ? selections.selectedOrgTypes : [];
  const orgTypeOk =
    orgKeys.length === 0 ||
    orgKeys.some((type) => orgMatchesOrgTypeFilterFlexible(org.org_type_id, type));

  const continentOk = continentFilterMatches(org, continentByCountryId, selections.selectedContinents || []);
  const countryOk = countryFilterMatches(org, selections.selectedCountries || []);

  const stKey = entityStatusKey(org);
  const statusOk = selections.selectedStatus.length === 0 || selections.selectedStatus.includes(stKey);

  return orgTypeOk && continentOk && countryOk && statusOk;
}

function personMatchesFilters(person, selections, continentByCountryId) {
  const continentOk = continentFilterMatches(person, continentByCountryId, selections.selectedContinents || []);
  const countryOk = countryFilterMatches(person, selections.selectedCountries || []);

  const stKey = entityStatusKey(person);
  const statusOk = selections.selectedStatus.length === 0 || selections.selectedStatus.includes(stKey);

  return continentOk && countryOk && statusOk;
}

function computeFilteredData(rows, tab, selections, continentByCountryId) {
  const orgKeys = Array.isArray(selections.selectedOrgTypes) ? selections.selectedOrgTypes : [];
  const noFilters =
    orgKeys.length === 0 &&
    selections.selectedContinents.length === 0 &&
    selections.selectedCountries.length === 0 &&
    selections.selectedStatus.length === 0;
  if (noFilters) return rows;

  if (tab === "organisation") {
    return rows.filter((org) => organisationMatchesFilters(org, selections, continentByCountryId));
  }

  return rows.filter((person) => personMatchesFilters(person, selections, continentByCountryId));
}

const AdvancedSearchComponent = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("organisation");

  const [data, setData] = useState([]);
  const [selectedOrgTypes, setSelectedOrgTypes] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedContinents, setSelectedContinents] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);

  const [loading, setLoading] = useState(true);
  /** Unique continent codes from MongoDB countries collection (sorted). */
  const [continents, setContinents] = useState([]);
  /** country _id string → continent code (from countries collection). */
  const [continentByCountryId, setContinentByCountryId] = useState({});
  const [tableFilter, setTableFilter] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const filtersActiveCount =
    (activeTab === "organisation" ? selectedOrgTypes.length : 0) +
    selectedContinents.length +
    selectedCountries.length +
    selectedStatus.length;

  const selectedFilters = useMemo(
    () => ({
      selectedOrgTypes,
      selectedCountries,
      selectedContinents,
      selectedOrgs,
      selectedRoles,
      selectedStatus,
    }),
    [selectedOrgTypes, selectedCountries, selectedContinents, selectedOrgs, selectedRoles, selectedStatus]
  );

  const filteredData = useMemo(
    () => computeFilteredData(data, activeTab, selectedFilters, continentByCountryId),
    [data, activeTab, selectedFilters, continentByCountryId]
  );

  const handleExcelDownload = () => {
  const rows =
    showSelectedOnly && selectedRows.length
      ? selectedRows
      : tableFilteredRows;

  if (!rows || rows.length === 0) {
    window.alert("No data to export.");
    return;
  }

  const formattedData = rows.map((r, index) => ({
    "#": index + 1,
    Name:
      activeTab === "organisation"
        ? r.name || ""
        : `${r.name || ""} ${r.mname || ""} ${r.lname || ""}`,
    "Org Type": formatOrgTypesLabel(r.org_type_id) || "-",
    Email: r.email || "-",
    "Country Code": r.country_id?.code || "-",
  }));

  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const file = new Blob([excelBuffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(file, `advanced-search-${activeTab}.xlsx`);
};
  
const handlePDFDownload = () => {
  const rows = showSelectedOnly && selectedRows.length
    ? selectedRows
    : tableFilteredRows;

  if (!rows || rows.length === 0) {
    window.alert("No data to export.");
    return;
  }

  const doc = new jsPDF();

  const title =
    activeTab === "organisation"
      ? "Organisation PDF"
      : "Individuals PDF";

  doc.setFontSize(16);
  doc.text(title, 14, 15);

  const headers = [
    "#",
    "Name",
    "Org Type",
    "Email",
    "Country Code",
  ];

  const body = rows.map((r, index) => [
    index + 1,
    activeTab === "organisation"
      ? r.name || ""
      : `${r.name || ""} ${r.mname || ""} ${r.lname || ""}`,
    formatOrgTypesLabel(r.org_type_id) || "-",
    r.email || "-",
    r.country_id?.code || "-",
  ]);

  autoTable(doc, {
    head: [headers],
    body: body,
    startY: 25,
    styles: { fontSize: 8 },
  });

  doc.save(`${title}.pdf`);
};

  const handleCSVDownload = () => {
  const rows =
    showSelectedOnly && selectedRows.length
      ? selectedRows
      : tableFilteredRows;

  if (!rows || rows.length === 0) {
    window.alert("No data to export.");
    return;
  }

  const formattedData = rows.map((r, index) => ({
    "#": index + 1,
    Name:
      activeTab === "organisation"
        ? r.name || ""
        : `${r.name || ""} ${r.mname || ""} ${r.lname || ""}`,
    "Org Type": formatOrgTypesLabel(r.org_type_id) || "-",
    Email: r.email || "-",
    "Country Code": r.country_id?.code || "-",
  }));

  const worksheet = XLSX.utils.json_to_sheet(formattedData);

  const csv = XLSX.utils.sheet_to_csv(worksheet);

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

  saveAs(blob, `advanced-search-${activeTab}.csv`);
};

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url = activeTab === "organisation" ? `${API_URL}/organisations` : `${API_URL}/persons`;
      const res = await axios.get(url);
      const rows = Array.isArray(res.data) ? res.data : [];
      setData(rows);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/countries`);
        const list = Array.isArray(res.data) ? res.data : [];
        if (cancelled) return;
        const byId = {};
        for (const c of list) {
          if (c?._id != null) byId[String(c._id)] = c.continent != null && c.continent !== "" ? String(c.continent) : "";
        }
        setContinentByCountryId(byId);
        const uniq = [...new Set(list.map((c) => c.continent).filter((x) => x != null && x !== ""))].sort((a, b) =>
          String(a).localeCompare(String(b))
        );
        setContinents(uniq);
      } catch (err) {
        console.error("Error fetching countries for continent filters:", err);
        if (!cancelled) {
          setContinentByCountryId({});
          setContinents([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSelectedOrgTypes([]);
    setSelectedCountries([]);
    setSelectedContinents([]);
    setSelectedOrgs([]);
    setSelectedRoles([]);
    setSelectedStatus([]);
    setTableFilter("");
    setSelectedRows([]);
    setShowSelectedOnly(false);
    fetchData();
  }, [fetchData]);

  const toggleSelection = (setter, value) => {
    setter((prev) => {
      const alreadySelected = prev.includes(value);
      return alreadySelected ? prev.filter((item) => item !== value) : [...prev, value];
    });
  };

  const clearSelection = (setter) => setter([]);

  const clearFilters = useCallback(() => {
    setSelectedOrgTypes([]);
    setSelectedCountries([]);
    setSelectedContinents([]);
    setSelectedOrgs([]);
    setSelectedRoles([]);
    setSelectedStatus([]);
    setTableFilter("");
    setShowSelectedOnly(false);
  }, []);

  const normalize = (v) => (v ?? "").toString().toLowerCase();

  const tableFilteredRows = useMemo(() => {
    const q = normalize(tableFilter).trim();
    if (!q) return filteredData;
    if (activeTab === "organisation") {
      return filteredData.filter(
        (row) =>
          normalize(row.name).includes(q) ||
          normalize(row.email).includes(q) ||
          normalize(formatOrgTypesLabel(row.org_type_id)).includes(q) ||
          normalize(row.country_id?.code).includes(q)
      );
    }
    return filteredData.filter(
      (row) =>
        normalize(row.name).includes(q) ||
        normalize(row.email).includes(q) ||
        normalize(row.mname).includes(q) ||
        normalize(row.lname).includes(q)
    );
  }, [filteredData, tableFilter, activeTab]);
  
  const exportRows = (showSelectedOnly && selectedRows.length
  ? selectedRows
  : tableFilteredRows
).map((r, index) => ({
  "#": index + 1,
  Name:
    activeTab === "organisation"
      ? r.name || ""
      : `${r.name || ""} ${r.mname || ""} ${r.lname || ""}`,
  "Org Type": formatOrgTypesLabel(r.org_type_id) || "-",
  Email: r.email || "-",
  "Country Code": r.country_id?.code || "-",
}));

  const displayData = useMemo(() => {
    if (showSelectedOnly && selectedRows.length > 0) {
      const ids = new Set(selectedRows.map((r) => String(r._id)));
      return tableFilteredRows.filter((r) => ids.has(String(r._id)));
    }
    return tableFilteredRows;
  }, [tableFilteredRows, showSelectedOnly, selectedRows]);

  const orgTypeBuckets = useMemo(
    () => (activeTab === "organisation" ? buildOrgTypeBuckets(data) : []),
    [data, activeTab]
  );
  const continentBuckets = useMemo(
    () => buildContinentBuckets(data, continentByCountryId, continents),
    [data, continentByCountryId, continents]
  );
  const countryBuckets = useMemo(() => buildCountryBuckets(data), [data]);

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    window.alert("Email copied!");
  };

  const handleDeleteOrg = useCallback(async (org) => {
    if (!window.confirm(`Delete "${org.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/organisations/${org._id}`);
      setData((prev) => prev.filter((o) => o._id !== org._id));
    } catch (err) {
      console.error("Delete failed:", err);
      window.alert("Failed to delete organisation.");
    }
  }, []);

  const organisationColumns = useMemo(
    () => [
      {
        name: "#",
        cell: (row, index) => index + 1,
        width: "60px",
      },
      {
        name: "Logo",
        cell: (row) => (
          <img
            className="adv-search-cell-logo"
            src={row.logo ? `/images/${row.logo}` : "/images/org_logo1.png"}
            alt={row.name || "Logo"}
          />
        ),
        width: "80px",
      },
      {
        name: "Name",
        selector: (row) => row.name,
        grow: 2,
        sortable: true,
        cell: (row) => (
          <span
            role="link"
            tabIndex={0}
            className="name-cell-link"
            onClick={() => navigate(`/organisation/${row._id}`, { state: { organisation: row } })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/organisation/${row._id}`, { state: { organisation: row } });
              }
            }}
          >
            {row.name || "Unnamed Organisation"}
          </span>
        ),
      },
      {
        name: "Org Type",
        grow: 2,
        selector: (row) => formatOrgTypesLabel(row.org_type_id),
        sortable: true,
        cell: (row) => formatOrgTypesLabel(row.org_type_id),
      },
      {
        name: "Email",
        selector: (row) => row.email || "-",
        sortable: true,
        grow: 2,
        cell: (row) => (
          <div className="email-copy-cell">
            <span className="email-text">{row.email}</span>
            <button
              type="button"
              className="email-copy-btn"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(row.email);
              }}
              title="Copy Email"
            >
              <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
            </button>
          </div>
        ),
      },
      {
        name: "Country Code",
        selector: (row) => row.country_id?.code || "N/A",
        sortable: true,
        cell: (row) => row.country_id?.code || "N/A",
      },
    ],
    [navigate]
  );

  const personColumns = useMemo(
    () => [
      {
        name: "#",
        cell: (row, index) => index + 1,
        width: "60px",
      },
      {
        name: "Logo",
        cell: (row) => (
          <img
            className="adv-search-cell-logo adv-search-cell-logo--round"
            src={resolvePersonImageUrl(row.image, API_URL) || "/images/avatar.png"}
            alt=""
          />
        ),
        width: "80px",
      },
      {
        name: "Name",
        selector: (row) => `${row.name || ""} ${row.mname || ""} ${row.lname || ""}`.trim(),
        cell: (row) => (
          <span
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/edit-person/${row._id}`, { state: { person: row } })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/edit-person/${row._id}`, { state: { person: row } });
              }
            }}
            className="name-cell-link"
          >
            {`${row.name || ""} ${row.mname || ""} ${row.lname || ""}`.trim() || "—"}
          </span>
        ),
        sortable: true,
      },
      {
        name: "Org Type",
        selector: () => "—",
        sortable: false,
        cell: () => "—",
      },
      {
        name: "Email",
        selector: (row) => row.email || "-",
        sortable: true,
        cell: (row) => (
          <div className="email-copy-cell">
            <span className="email-text">{row.email}</span>
            <button
              type="button"
              className="email-copy-btn"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(row.email);
              }}
              title="Copy Email"
            >
              <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
            </button>
          </div>
        ),
      },
      {
        name: "Country Code",
        selector: (row) => row.country_id?.code || "N/A",
        sortable: true,
        cell: (row) => row.country_id?.code || "N/A",
      },
    ],
    [navigate]
  );

  const columns = activeTab === "organisation" ? organisationColumns : personColumns;

  const tableData = useMemo(
    () => ({
      columns,
      data: displayData,
      filter: false,
      export: true,
      print: true,
      exportHeaders: true,
      fileName: `advanced-search-${activeTab}`,
    }),
    [columns, displayData, activeTab]
  );

  const handleCopy = async () => {
  const rows = showSelectedOnly && selectedRows.length
    ? selectedRows
    : tableFilteredRows;

  if (!rows || rows.length === 0) {
    window.alert("No data to copy.");
    return;
  }

  const headers = [
    "#",
    "Name",
    "Org. Type",
    "Email",
    "Country Code",
    "Status"
  ];

  const formattedRows = rows.map((r, index) => [
    index + 1,
    r.name || "",
    formatOrgTypesLabel(r.org_type_id) || "",
    r.email || "",
    r.country_id?.code || "",
    r.status === 1 ? "Active" : "Inactive"
  ]);

  const textToCopy = [
    "IGF :: Advanced Search",
    "",
    headers.join("\t"),
    ...formattedRows.map(row => row.join("\t"))
  ].join("\n");

  try {
    await navigator.clipboard.writeText(textToCopy);
    window.alert(`Copied ${rows.length} row(s) to clipboard.`);
  } catch (e) {
    console.error(e);
    window.alert("Could not copy to clipboard.");
  }
};
  
  const handlePrint = () => {
  const rows = showSelectedOnly && selectedRows.length
    ? selectedRows
    : tableFilteredRows;

  if (!rows || rows.length === 0) {
    window.alert("No data to print.");
    return;
  }

  const title =
    activeTab === "organisation"
      ? "Organisation Report"
      : "Individuals Report";

  const tableRows = rows.map((r, index) => `
    <tr>
      <td>${index + 1}</td>
      <td>${
        activeTab === "organisation"
          ? r.name || ""
          : `${r.name || ""} ${r.mname || ""} ${r.lname || ""}`
      }</td>
      <td>${formatOrgTypesLabel(r.org_type_id) || "-"}</td>
      <td>${r.email || "-"}</td>
      <td>${r.country_id?.code || "-"}</td>
    </tr>
  `).join("");

  const html = `
    <html>
      <head>
        <title>${title}</title>
        <style>
         @page { size: A4 landscape; margin: 20px; }
          body { font-family: Arial; padding: 20px; }
          h2 { text-align: center; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 8px; font-size: 12px; }
          th { background: #d4af37; color: white; }
        </style>
      </head>
      <body>
        <h2>${title}</h2>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Org Type</th>
              <th>Email</th>
              <th>Country Code</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.print();
};

   const toggleShowSelected = () => {
    if (!selectedRows.length) {
      window.alert("Select one or more rows first.");
      return;
    }
    setShowSelectedOnly((prev) => !prev);
  };

  const renderFilterSection = (title, items, selected, setter) => {
    const hasSelection = selected.length > 0;

    return (
      <div className="adv-filter-section">
        <div className="adv-filter-section-head">
          <span>{title}</span>
          {hasSelection && (
            <button
              type="button"
              className="adv-filter-section-clear"
              aria-label={`Clear ${title}`}
              onClick={() => clearSelection(setter)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        <div className="adv-filter-scroll">
          <ul className="adv-filter-list">
            {items.map((item) => {
              const active = selected.includes(item.key);
              return (
                <li key={item.key}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`filter-item${active ? " active" : ""}`}
                    onClick={() => toggleSelection(setter, item.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleSelection(setter, item.key);
                      }
                    }}
                  >
                    <span className="filter-item-label">{item.label}</span>
                    <span className="filter-item-count">{item.count}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="adv-search-page">
      <div className="adv-search-body">
        <div className="adv-search-left-tabs" role="tablist" aria-label="Search mode">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "organisation"}
            className={`btn px-4 py-3 ${activeTab === "organisation" ? "btn-warning" : "btn-success"}`}
            onClick={() => setActiveTab("organisation")}
          >
            Search Organisation
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "person"}
            className={`btn px-4 py-3 ${activeTab === "person" ? "btn-warning" : "btn-success"}`}
            onClick={() => setActiveTab("person")}
          >
            Search Individuals
          </button>
        </div>

        <aside className="adv-search-sidebar">
          <div className="adv-search-sidebar-header">
            <span className="adv-search-filters-meta">Filters Active - {filtersActiveCount}</span>
            <button type="button" className="adv-search-clear-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>

          <div className="adv-search-sidebar-inner">
            {loading ? (
              <div className="adv-search-empty-hint">Loading…</div>
            ) : (
              <>
                {activeTab === "organisation" &&
                  renderFilterSection("Org Type", orgTypeBuckets, selectedOrgTypes, setSelectedOrgTypes)}
                {renderFilterSection("Continent", continentBuckets, selectedContinents, setSelectedContinents)}
                {renderFilterSection("Country", countryBuckets, selectedCountries, setSelectedCountries)}
              </>
            )}
          </div>
        </aside>

        <div className="adv-search-action-bar">
          <div className="adv-search-action-buttons">
            <button type="button" className="btn btn-success px-5 py-3 adv-search-action-btn" onClick={handleCopy}>
              Copy
            </button>
            <button type="button" className="btn btn-success px-5 py-3 adv-search-action-btn" onClick={handlePrint}>
              Print
            </button>
            <button
              type="button"
              className="btn btn-success px-5 py-3 adv-search-action-btn"
              onClick={handlePDFDownload}
            >
              PDF
            </button>
            <button
              type="button"
              className="btn btn-success px-5 py-3 adv-search-action-btn"
              onClick={handleExcelDownload}
            >
              Excel
            </button>
            <button type="button" className="btn btn-success px-5 py-3 adv-search-action-btn" onClick={handleCSVDownload}>
              CSV
            </button>
            <button
              type="button"
              className={`btn btn-success px-4 py-3 adv-search-action-btn ${showSelectedOnly ? "btn-warning" : "btn-success"} `}
              onClick={toggleShowSelected}
            >
              {showSelectedOnly ? "Show All" : "Show Selected"}
            </button>
          </div>

          <div className="adv-search-show-wrap">
            <span>Show</span>
            <select
              aria-label="Rows per page"
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <main className="adv-search-main">
          <div className="adv-search-table-toolbar">
            <input
              id="adv-search-table-filter"
              type="search"
              className="adv-search-table-filter-input"
              placeholder={activeTab === "organisation" ? "Search Organisation" : "Search Individuals"}
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
            />
          </div>

          <div id="print-section" className="adv-search-table-wrap crm-orgns-table-wrapper">
            <DataTableExtensions {...tableData}>
              <DataTable
                key={`${activeTab}-${rowsPerPage}`}
                progressPending={loading}
                pagination
                paginationPerPage={rowsPerPage}
                paginationComponentOptions={{ noRowsPerPage: true }}
                highlightOnHover={false}
                striped={false}
                responsive
                fixedHeader={false}
                customStyles={advancedSearchTableStyles}
                selectableRows
                selectableRowsHighlight
                selectableRowsNoSelectAll
                onSelectedRowsChange={({ selectedRows }) => {
                  setSelectedRows(selectedRows);
                }}
                noDataComponent={
                  <div className="adv-search-empty-hint">
                    {loading ? "Loading…" : data.length === 0 ? "No records found" : "No matching rows"}
                  </div>
                }
              />
            </DataTableExtensions>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdvancedSearchComponent;